import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EditIcon from "@mui/icons-material/Edit";
import QrCodeIcon from "@mui/icons-material/QrCode";
import HistoryIcon from "@mui/icons-material/History";
import UploadIcon from "@mui/icons-material/Upload";
import MoveDownIcon from "@mui/icons-material/MoveDown";

function ProductsInventoryDetails() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [folderPath, setFolderPath] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(
          `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}`
        );
        setProduct(res.data);
        setFormData(res.data);
      } catch (err) {
        console.error("Eroare la fetch detalii produs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    const fetchFolderPath = async () => {
      try {
        const res = await axios.get(
          `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}/folder-path`
        );
        setFolderPath(res.data);
      } catch (err) {
        console.error("Eroare la fetch folder path:", err);
      }
    };
    fetchFolderPath();
  }, [productId]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("image", file);
    try {
      const uploadRes = await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/images/upload-to-s3",
        form
      );
      const imageUrl = uploadRes.data.image_url;
      await axios.patch(
        `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}/field`,
        {
          field: "image",
          value: imageUrl,
        }
      );
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      setProduct((prev) => ({ ...prev, image_url: imageUrl }));
    } catch (err) {
      console.error("Eroare la actualizare imagine:", err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(
        `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}`,
        {
          ...formData,
          image: formData.image_base64 || null,
        }
      );
      setProduct({ ...product, ...formData });
      setFormData((prev) => ({
        ...prev,
        image_base64: formData.image_base64 || product.image_base64,
      }));
      setEditOpen(false);
    } catch (err) {
      console.error("Eroare la actualizare produs:", err);
    }
  };

  const handleGenerateLabel = async () => {
    try {
      if (!product?.sku || String(product.sku).trim() === "") {
        window.alert("Completați mai întâi SKU-ul produsului.");
        return;
      }
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}/label`
      );
      const { url } = res.data || {};
      if (url) {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Eroare la generarea etichetei:", error);
    }
  };

  const EditableField = ({ label, value, fieldKey }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);

    const saveChange = async () => {
      try {
        await axios.patch(
          `https://server-9p4hx.kinsta.app/api/stocmanager/products/${productId}/field`,
          {
            field: fieldKey,
            value: localValue,
          }
        );
        setProduct((prev) => ({ ...prev, [fieldKey]: localValue }));
        setFormData((prev) => ({ ...prev, [fieldKey]: localValue }));
      } catch (err) {
        console.error("Eroare la salvare:", err);
      }
      setEditing(false);
    };

    return (
      <Box
        mb={2}
        onClick={() => !editing && setEditing(true)}
        sx={{ cursor: editing ? "default" : "pointer" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, display: "block" }}
        >
          {label}
        </Typography>
        {editing ? (
          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              fullWidth
              value={localValue || ""}
              onChange={(e) => setLocalValue(e.target.value)}
              sx={{ mr: 1 }}
            />
            <Button variant="contained" size="small" onClick={saveChange}>
              OK
            </Button>
          </Box>
        ) : (
          <Typography
            fontWeight={fieldKey === "product_name" ? 700 : 600}
            sx={{ fontSize: fieldKey === "product_name" ? 20 : 14 }}
          >
            {fieldKey === "price" ? `${value} RON` : value || "-"}
          </Typography>
        )}
      </Box>
    );
  };

  if (loading)
    return (
      <Container sx={{ pt: 4 }}>
        <CircularProgress />
      </Container>
    );
  if (!product)
    return (
      <Container sx={{ pt: 4 }}>
        <Typography>Produsul nu a fost găsit.</Typography>
      </Container>
    );

  return (
    <Container maxWidth={false} sx={{ pt: 2, px: { xs: 1.5, md: 3 } }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Detalii produs
        </Typography>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
      </Box>

      <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
        {/* Image wrapper */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 320,
            overflow: "hidden",
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={
              formData.image_url ||
              product.image_url ||
              formData.image_base64 ||
              product.image_base64
            }
            alt={product.product_name}
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 67%",
            }}
          />
          <IconButton
            component="label"
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "#fff",
              boxShadow: 1,
            }}
          >
            <EditIcon />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </IconButton>
        </Box>

        {/* Path */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ mt: 1, mb: 1 }}
          color="text.secondary"
        >
          <Typography fontSize={16}>
            {folderPath?.layer1?.name}
            {folderPath?.layer1?.name && folderPath?.layer2?.name && " • "}
            {folderPath?.layer2?.name}
          </Typography>
        </Box>

        {/* Editable fields */}
        <EditableField
          label="Titlu produs"
          value={product.product_name}
          fieldKey="product_name"
        />
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <EditableField label="SKU" value={product.sku} fieldKey="sku" />
          </Grid>
          <Grid item md={6} xs={12}>
            <EditableField
              label="Preț"
              value={product.price}
              fieldKey="price"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <EditableField
              label="Cantitate"
              value={product.quantity}
              fieldKey="quantity"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <EditableField label="Tags" value={product.tags} fieldKey="tags" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        <EditableField label="Notițe" value={product.notes} fieldKey="notes" />
        <Divider sx={{ my: 1.5 }} />
        <EditableField
          label="Link"
          value={product.web_link}
          fieldKey="web_link"
        />

        <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
          <Button
            variant="outlined"
            startIcon={<QrCodeIcon />}
            onClick={handleGenerateLabel}
          >
            Etichetă
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
          >
            Editează
          </Button>
        </Box>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth>
        <DialogTitle>Editează Produs</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Nume produs"
            value={formData.product_name || ""}
            onChange={(e) =>
              setFormData({ ...formData, product_name: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="SKU"
            value={formData.sku || ""}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Preț (€)"
            type="number"
            value={formData.price || ""}
            onChange={(e) =>
              setFormData({ ...formData, price: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Cantitate"
            type="number"
            value={formData.quantity || ""}
            onChange={(e) =>
              setFormData({ ...formData, quantity: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Tags"
            value={formData.tags || ""}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Link Web"
            value={formData.web_link || ""}
            onChange={(e) =>
              setFormData({ ...formData, web_link: e.target.value })
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Notițe"
            multiline
            value={formData.notes || ""}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
          />
          <Button component="label" variant="outlined" sx={{ mt: 2 }}>
            Încarcă o imagine nouă
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ProductsInventoryDetails;
